import React, { useState, useEffect } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { useSelector } from "react-redux";

// Import existing React Icons used in the project
import { FaHome, FaClock, FaSpinner, FaUndo, FaKey } from "react-icons/fa";
import { FaTrash } from "react-icons/fa6";
import {
  MdWaterDrop,
  MdCreditScore,
  MdPayment,
  MdOutlineTipsAndUpdates,
  MdSecurity,
} from "react-icons/md";
import { LiaFileInvoiceSolid } from "react-icons/lia";
import { RxDashboard } from "react-icons/rx";
import { IoPersonOutline } from "react-icons/io5";

const TenantTourTablet = ({
  runTour,
  onTourEnd,
  sharedValue,
  setSharedValue,
  resumeAtStep,
}) => {
  const { user } = useSelector((state) => state.landlordslice);
  const [tourState, setTourState] = useState({
    run: false,
    stepIndex: 0,
    steps: [],
  });

  // Define tour steps for Tablet devices
  const tourSteps = [
    {
      target: "body",
      content: (
        <div>
          <h3
            style={{
              color: "#192A3A",
              marginBottom: "14px",
              fontFamily: "Karla, sans-serif",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textAlign: "center",
              fontSize: "18px",
            }}
          >
            <MdSecurity style={{ fontSize: "20px", color: "#B39359" }} />
            Welcome! Let's Secure Your Account
          </h3>
          <p
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "15px",
              lineHeight: "1.5",
            }}
          >
            Welcome to Rently Buzz! For your security, we highly recommend
            changing your password to something more secure and personal.
          </p>
          <div
            style={{
              marginTop: "14px",
              padding: "12px",
              backgroundColor: "#f8f6f3",
              borderRadius: "8px",
              border: "1px solid #B39359",
            }}
          >
            <strong
              style={{
                fontFamily: "Karla, sans-serif",
                color: "#192A3A",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "8px",
                fontSize: "16px",
              }}
            >
              <FaKey style={{ fontSize: "16px", color: "#B39359" }} />
              Password Security Tips:
            </strong>
            <ul
              style={{
                marginTop: "8px",
                paddingLeft: "20px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
              }}
            >
              <li>Use at least 8 characters</li>
              <li>Include uppercase and lowercase letters</li>
              <li>Add numbers and special characters</li>
              <li>Avoid personal information</li>
            </ul>
          </div>
          <p
            style={{
              marginTop: "12px",
              fontSize: "14px",
              color: "#6b7280",
              fontFamily: "Poppins, sans-serif",
              fontStyle: "italic",
              textAlign: "center",
            }}
          >
            Tap "Next" to go to your profile page and change your password.
          </p>
        </div>
      ),
      placement: "center",
      disableBeacon: true,
      styles: {
        options: {
          primaryColor: "#192A3A",
        },
      },
    },
    {
      target: ".change-password-button",
      content: (
        <div>
          <h3
            style={{
              color: "#192A3A",
              marginBottom: "14px",
              fontFamily: "Karla, sans-serif",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textAlign: "center",
              fontSize: "18px",
            }}
          >
            <FaKey style={{ fontSize: "20px", color: "#B39359" }} />
            Change Your Password
          </h3>
          <p
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "15px",
              lineHeight: "1.5",
            }}
          >
            Here's your "Change Password" button! Tap this button to update your
            password to something more secure and personal.
          </p>
          <div
            style={{
              marginTop: "14px",
              padding: "12px",
              backgroundColor: "#f8f6f3",
              borderRadius: "8px",
              border: "1px solid #B39359",
            }}
          >
            <strong
              style={{
                fontFamily: "Karla, sans-serif",
                color: "#192A3A",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "10px",
                fontSize: "16px",
              }}
            >
              <MdSecurity style={{ fontSize: "16px", color: "#B39359" }} />
              Why Change Your Password?
            </strong>
            <ul
              style={{
                marginTop: "8px",
                paddingLeft: "20px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
              }}
            >
              <li>Protects your personal and financial information</li>
              <li>Prevents unauthorized access to your account</li>
              <li>Ensures your rental payments and data stay secure</li>
            </ul>
          </div>
          <p
            style={{
              marginTop: "12px",
              fontSize: "14px",
              color: "#6b7280",
              fontFamily: "Poppins, sans-serif",
              fontStyle: "italic",
              textAlign: "center",
            }}
          >
            You can change your password now or continue the tour and do it
            later.
          </p>
        </div>
      ),
      placement: "top",
      styles: {
        options: {
          primaryColor: "#192A3A",
        },
      },
    },
    {
      target: ".cards-container",
      content: (
        <div>
          <h3
            style={{
              color: "#192A3A",
              marginBottom: "14px",
              fontFamily: "Karla, sans-serif",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textAlign: "center",
              fontSize: "18px",
            }}
          >
            <RxDashboard style={{ fontSize: "20px", color: "#B39359" }} />
            Payment Progress Dashboard
          </h3>
          <p
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "15px",
              lineHeight: "1.5",
            }}
          >
            Welcome to your payment dashboard! These information cards provide a
            comprehensive overview of your payment progress and financial
            status.
          </p>
          <ul
            style={{
              marginTop: "12px",
              paddingLeft: "20px",
              fontFamily: "Poppins, sans-serif",
              fontSize: "14px",
              lineHeight: "1.4",
            }}
          >
            <li>
              <strong>Invoices Pending:</strong> Shows outstanding payments you
              need to make
            </li>
            <li>
              <strong>Invoices Paid:</strong> Displays your completed payments
              and history
            </li>
            <li>
              <strong>Progress Indicators:</strong> Visual representation of
              payment completion
            </li>
            <li>
              <strong>Payment Categories:</strong> Rent, water, arrears, and
              other charges
            </li>
          </ul>
          <p
            style={{
              marginTop: "12px",
              fontSize: "13px",
              color: "#6b7280",
              fontFamily: "Poppins, sans-serif",
              fontStyle: "italic",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <MdOutlineTipsAndUpdates
              style={{ fontSize: "24px", color: "#B39359" }}
            />
            Tip: These cards update in real-time as you make payments!
          </p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      styles: {
        options: {
          primaryColor: "#192A3A",
          width: 350,
        },
        tooltip: {
          fontSize: 14,
        },
        buttonNext: {
          fontSize: 13,
          padding: "10px 20px",
        },
        buttonBack: {
          fontSize: 13,
          padding: "10px 20px",
        },
      },
    },
    {
      target: ".quick-links-container",
      content: (
        <div>
          <h3
            style={{
              color: "#B39359",
              marginBottom: "14px",
              fontFamily: "Karla, sans-serif",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textAlign: "center",
              fontSize: "18px",
            }}
          >
            <MdPayment style={{ fontSize: "20px", color: "#192A3A" }} />
            Quick Payment Links
          </h3>
          <p
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "15px",
              lineHeight: "1.5",
            }}
          >
            Need to make a payment quickly? These quick links allow you to pay
            specific bills directly from the overview page without navigating to
            different sections.
          </p>
          <div
            style={{
              marginTop: "14px",
              padding: "12px",
              backgroundColor: "#f8f6f3",
              borderRadius: "8px",
              border: "1px solid #B39359",
            }}
          >
            <strong
              style={{
                fontFamily: "Karla, sans-serif",
                color: "#192A3A",
                display: "block",
                textAlign: "center",
                marginBottom: "10px",
                fontSize: "14px",
              }}
            >
              Available quick payments:
            </strong>
            <ul
              style={{
                marginTop: "8px",
                paddingLeft: "20px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "13px",
                lineHeight: "1.4",
              }}
            >
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "6px",
                }}
              >
                <FaHome style={{ color: "#B39359", fontSize: "14px" }} />
                <strong>Rent</strong> - Monthly rent payments
              </li>
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "6px",
                }}
              >
                <MdWaterDrop style={{ color: "#B39359", fontSize: "14px" }} />
                <strong>Water</strong> - Water bill payments
              </li>
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "6px",
                }}
              >
                <FaClock style={{ color: "#B39359", fontSize: "14px" }} />
                <strong>Arrears</strong> - Outstanding balance payments
              </li>
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "6px",
                }}
              >
                <LiaFileInvoiceSolid
                  style={{ color: "#B39359", fontSize: "14px" }}
                />
                <strong>Service Charge</strong> - Property service fees
              </li>
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "6px",
                }}
              >
                <FaSpinner style={{ color: "#B39359", fontSize: "14px" }} />
                <strong>Unpaid Deposit</strong> - Security deposit payments
              </li>
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FaTrash style={{ color: "#B39359", fontSize: "14px" }} />
                <strong>Garbage</strong> - Waste management fees
              </li>
            </ul>
          </div>
          <p
            style={{
              marginTop: "12px",
              fontSize: "13px",
              color: "#6b7280",
              fontFamily: "Poppins, sans-serif",
              fontStyle: "italic",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <MdOutlineTipsAndUpdates
              style={{ fontSize: "24px", color: "#B39359" }}
            />
            Tap any card to open a payment modal with pre-filled amounts!
          </p>
        </div>
      ),
      placement: "top",
      styles: {
        options: {
          primaryColor: "#B39359",
          width: 350,
        },
        tooltip: {
          fontSize: 14,
        },
        buttonNext: {
          fontSize: 13,
          padding: "10px 20px",
        },
        buttonBack: {
          fontSize: 13,
          padding: "10px 20px",
        },
      },
    },
    {
      target: ".transactions-container",
      content: (
        <div>
          <h3
            style={{
              color: "#192A3A",
              marginBottom: "14px",
              fontFamily: "Karla, sans-serif",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textAlign: "center",
              fontSize: "18px",
            }}
          >
            <MdCreditScore style={{ fontSize: "20px", color: "#B39359" }} />
            Transaction History
          </h3>
          <p
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "15px",
              lineHeight: "1.5",
            }}
          >
            This section displays your recent payment transactions. You can view
            details about your payment history and track all completed
            transactions.
          </p>
          <div
            style={{
              marginTop: "14px",
              padding: "12px",
              backgroundColor: "#f4f2ef",
              borderRadius: "8px",
              border: "1px solid #192A3A",
            }}
          >
            <ul
              style={{
                paddingLeft: "20px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                lineHeight: "1.4",
              }}
            >
              <li>
                <strong>Payment Date:</strong> When the payment was processed
              </li>
              <li>
                <strong>Amount Paid:</strong> Total payment amount for each
                transaction
              </li>
              <li>
                <strong>Payment Category:</strong> Type of payment (rent, water,
                etc.)
              </li>
              <li>
                <strong>Transaction Status:</strong> Confirmation of successful
                payments
              </li>
              <li>
                <strong>Payment Method:</strong> How the payment was made
              </li>
            </ul>
          </div>
          <p
            style={{
              marginTop: "12px",
              fontSize: "13px",
              color: "#6b7280",
              fontFamily: "Poppins, sans-serif",
              fontStyle: "italic",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <MdOutlineTipsAndUpdates
              style={{ fontSize: "24px", color: "#B39359" }}
            />
            Transaction records are updated automatically and kept for your
            records!
          </p>
        </div>
      ),
      placement: "top",
      styles: {
        options: {
          primaryColor: "#192A3A",
          width: 350,
        },
        tooltip: {
          fontSize: 14,
        },
        buttonNext: {
          fontSize: 13,
          padding: "10px 20px",
        },
        buttonBack: {
          fontSize: 13,
          padding: "10px 20px",
        },
      },
    },
    {
      target: ".mobile-nav",
      content: (
        <div>
          <h3
            style={{
              color: "#B39359",
              marginBottom: "14px",
              fontFamily: "Karla, sans-serif",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textAlign: "center",
              fontSize: "18px",
            }}
          >
            <IoPersonOutline style={{ fontSize: "20px", color: "#192A3A" }} />
            Navigation Menu
          </h3>
          <p
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "15px",
              lineHeight: "1.5",
            }}
          >
            Use this bottom navigation bar to move between different sections of
            your tenant dashboard.
          </p>
          <div
            style={{
              marginTop: "14px",
              padding: "12px",
              backgroundColor: "#f8f6f3",
              borderRadius: "8px",
              border: "1px solid #B39359",
            }}
          >
            <ul
              style={{
                marginTop: "8px",
                paddingLeft: "20px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                lineHeight: "1.4",
              }}
            >
              <li>
                <strong>Overview:</strong> Payment dashboard with progress
                reports and quick links
              </li>
              <li>
                <strong>Payments:</strong> Make new payments for various charges
              </li>
              <li>
                <strong>Invoices:</strong> View all your invoices and billing
                details
              </li>
              <li>
                <strong>Messages:</strong> Communicate with property management
              </li>
              <li>
                <strong>Profile:</strong> Manage your account settings and
                personal information
              </li>
            </ul>
          </div>
          <p
            style={{
              marginTop: "12px",
              fontSize: "14px",
              color: "#192A3A",
              fontFamily: "Poppins, sans-serif",
              fontWeight: "600",
              textAlign: "center",
              backgroundColor: "#f8f6f3",
              padding: "8px",
              borderRadius: "6px",
            }}
          >
            🎉 Congratulations! Tour complete! You're now ready to efficiently
            manage all your payments and tenant activities!
          </p>
        </div>
      ),
      placement: "top",
      styles: {
        options: {
          primaryColor: "#B39359",
          width: 350,
        },
        tooltip: {
          fontSize: 14,
        },
        buttonNext: {
          fontSize: 13,
          padding: "10px 20px",
        },
        buttonBack: {
          fontSize: 13,
          padding: "10px 20px",
        },
      },
    },
  ];

  useEffect(() => {
    if (runTour) {
      setTourState({
        run: true,
        stepIndex: resumeAtStep !== null ? resumeAtStep : 0,
        steps: tourSteps,
      });
    }
  }, [runTour, resumeAtStep]);

  const handleJoyrideCallback = (data) => {
    const { action, index, status, type } = data;

    if (
      [STATUS.FINISHED, STATUS.SKIPPED].includes(status) ||
      action === ACTIONS.CLOSE
    ) {
      setTourState({ ...tourState, run: false });

      // Mark tour as completed for this user
      localStorage.setItem(
        `tenant_tour_completed_tablet_${user?.data?.user?.user_id}`,
        "true"
      );

      if (onTourEnd) {
        onTourEnd();
      }
    } else if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);

      // Special handling for switching between Profile and Dashboard views
      if (index === 0 && action === ACTIONS.NEXT) {
        // Switch to profile view when moving from welcome step to password step
        setSharedValue("Profile");

        // Delay the step progression to allow view switch
        setTimeout(() => {
          setTourState({ ...tourState, stepIndex: nextStepIndex });
        }, 500);
      } else if (index === 1 && action === ACTIONS.NEXT) {
        // Switch back to dashboard when moving from password step to dashboard tour
        setSharedValue("Dashboard");

        // Delay the step progression to allow view switch
        setTimeout(() => {
          setTourState({ ...tourState, stepIndex: nextStepIndex });
        }, 500);
      } else if (index === 2 && action === ACTIONS.PREV) {
        // Switch back to profile when going back from dashboard to password step
        setSharedValue("Profile");

        // Delay the step progression to allow view switch
        setTimeout(() => {
          setTourState({ ...tourState, stepIndex: nextStepIndex });
        }, 500);
      } else if (index === 1 && action === ACTIONS.PREV) {
        // Switch back to dashboard when going back from password step to welcome step
        setSharedValue("Dashboard");

        // Delay the step progression to allow view switch
        setTimeout(() => {
          setTourState({ ...tourState, stepIndex: nextStepIndex });
        }, 500);
      } else {
        setTourState({ ...tourState, stepIndex: nextStepIndex });
      }
    }
  };

  const joyrideStyles = {
    options: {
      arrowColor: "#fff",
      backgroundColor: "#fff",
      overlayColor: "rgba(0, 0, 0, 0.4)",
      primaryColor: "#192A3A",
      textColor: "#333",
      width: 380,
      zIndex: 1000,
    },
    tooltip: {
      fontSize: 15,
      padding: 24,
      borderRadius: 10,
    },
    tooltipContainer: {
      textAlign: "left",
    },
    tooltipTitle: {
      fontSize: 18,
      fontWeight: "bold",
      marginBottom: 12,
    },
    buttonNext: {
      backgroundColor: "#192A3A",
      fontSize: 14,
      fontWeight: "600",
      padding: "12px 24px",
      borderRadius: 8,
    },
    buttonBack: {
      color: "#192A3A",
      fontSize: 14,
      fontWeight: "600",
      padding: "12px 24px",
      borderRadius: 8,
    },
    buttonSkip: {
      color: "#6b7280",
      fontSize: 13,
    },
    beacon: {
      inner: "#192A3A",
      outer: "#192A3A",
    },
  };

  if (!runTour) return null;

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous={true}
      hideCloseButton={false}
      run={tourState.run}
      scrollToFirstStep={true}
      showProgress={true}
      showSkipButton={true}
      stepIndex={tourState.stepIndex}
      steps={tourState.steps}
      styles={joyrideStyles}
      locale={{
        back: "Back",
        close: "Close",
        last: "Finish Tour",
        next: "Next",
        open: "Open the dialog",
        skip: "Skip Tour",
      }}
      disableOverlayClose={true}
      spotlightClicks={false}
      disableScrolling={false}
    />
  );
};

export default TenantTourTablet;
